import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import EmailAnalyticsBar from './email/EmailAnalyticsBar';
import TemplatesCard from './email/TemplatesCard';
import ImportEmailListModal from './email/ImportEmailListModal';
import EmailStatsModal from './email/EmailStatsModal';
import ComposeEmailCard from './email/ComposeEmailCard';
import ImportListCard from './email/ImportListCard';
import SavedListsCard from './email/SavedListsCard';
import ScheduledEmailsCard from './email/ScheduledEmailsCard';
import EmailHistoryCard from './email/EmailHistoryCard';
import SendGridHealthBadge from './email/SendGridHealthBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailCenterTab = () => {
  // Data
  const [emailRecipients, setEmailRecipients] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailAnalytics, setEmailAnalytics] = useState(null);
  const [savedLists, setSavedLists] = useState([]);
  const [scheduledList, setScheduledList] = useState([]);

  // Composer
  const [selectedRecipientType, setSelectedRecipientType] = useState('all');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [customEmails, setCustomEmails] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');

  // UI state
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importListName, setImportListName] = useState('');
  const [selectedEmailStats, setSelectedEmailStats] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // --------------- Data fetch ---------------
  const fetchEmailData = useCallback(async () => {
    try {
      const [recipientsRes, templatesRes, historyRes, analyticsRes, listsRes, schedRes] =
        await Promise.all([
          fetch(`${API_URL}/api/email/recipients`),
          fetch(`${API_URL}/api/email/templates`),
          fetch(`${API_URL}/api/email/history`),
          fetch(`${API_URL}/api/email/analytics`),
          fetch(`${API_URL}/api/email/lists`),
          fetch(`${API_URL}/api/email/scheduled`),
        ]);
      const [recipients, templates, history, analytics, lists, sched] = await Promise.all([
        recipientsRes.json(), templatesRes.json(), historyRes.json(),
        analyticsRes.json(), listsRes.json(), schedRes.json(),
      ]);
      setEmailRecipients(recipients);
      setEmailTemplates(templates.templates || []);
      setEmailHistory(history.history || []);
      setEmailAnalytics(analytics);
      setSavedLists(lists.lists || []);
      setScheduledList(sched.scheduled || []);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Email data fetch failed:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchEmailData();
  }, [fetchEmailData]);

  // --------------- Handlers ---------------
  const viewEmailStats = async (emailId) => {
    try {
      const response = await fetch(`${API_URL}/api/email/stats/${emailId}`);
      const data = await response.json();
      setSelectedEmailStats(data);
      setShowStatsModal(true);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.warn('Email stats fetch failed:', err.message);
    }
  };

  const deleteEmailHistory = async (emailId, subject, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete "${subject}" from history? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/email/history/${emailId}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to delete');
      setEmailHistory((prev) => prev.filter((h) => h.id !== emailId));
      setEmailStatus({ type: 'success', message: 'Email removed from history.' });
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to delete email.' });
    }
  };

  const loadTemplate = (template) => {
    setEmailSubject(template.subject);
    setEmailContent(template.content);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEmailStatus({
      type: 'success',
      message: `Loaded "${template.name || template.subject}" into the composer. Edit and send when ready.`,
    });
  };

  const reuseEmail = async (item, e) => {
    e?.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/email/stats/${item.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to load email');
      setEmailSubject(data.subject || item.subject || '');
      setEmailContent(data.message || '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setEmailStatus({
        type: 'success',
        message: 'Loaded into composer. Edit recipients/content, then click Send Email.',
      });
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to load email for reuse.' });
    }
  };

  const saveAsTemplate = async (item, e) => {
    e?.stopPropagation();
    const defaultName = (item?.subject || 'Untitled template').slice(0, 60);
    const name = window.prompt('Save this email as a template. Template name:', defaultName);
    if (!name || !name.trim()) return;
    try {
      let subject = item?.subject || '';
      let content = item?.message || '';
      if (item?.id && !content) {
        const res = await fetch(`${API_URL}/api/email/stats/${item.id}`);
        const data = await res.json();
        if (res.ok) {
          subject = data.subject || subject;
          content = data.message || content;
        }
      }
      const res = await fetch(`${API_URL}/api/email/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), subject, content }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to save template');
      setEmailStatus({ type: 'success', message: `Saved template "${name.trim()}".` });
      fetchEmailData();
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to save template.' });
    }
  };

  const deleteTemplate = async (template, e) => {
    e?.stopPropagation();
    if (template.builtin) {
      setEmailStatus({ type: 'error', message: 'Built-in templates cannot be deleted.' });
      return;
    }
    if (!window.confirm(`Delete template "${template.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/email/templates/${template.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to delete template');
      setEmailTemplates((prev) => prev.filter((t) => t.id !== template.id));
      setEmailStatus({ type: 'success', message: 'Template deleted.' });
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to delete template.' });
    }
  };

  const saveCurrentAsTemplate = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      setEmailStatus({ type: 'error', message: 'Enter a subject and message before saving as template.' });
      return;
    }
    await saveAsTemplate({ subject: emailSubject, message: emailContent });
  };

  const deleteSavedList = async (list, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete saved list "${list.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/email/lists/${list.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Failed to delete list');
      setSavedLists((prev) => prev.filter((l) => l.id !== list.id));
      if (selectedRecipientType === `list:${list.id}`) setSelectedRecipientType('all');
      setEmailStatus({ type: 'success', message: 'Saved list deleted.' });
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to delete list.' });
    }
  };

  const useSavedList = (list) => {
    setSelectedRecipientType(`list:${list.id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setEmailStatus({
      type: 'success',
      message: `Composer set to send to "${list.name}". Add subject + message and click Send.`,
    });
  };

  const buildSendPayload = () => {
    let recipientType = selectedRecipientType;
    let customEmailsPayload = customEmails
      .split(/[,;\s\n]+/)
      .map((e) => e.trim())
      .filter((e) => e);

    if (selectedRecipientType.startsWith('list:')) {
      const listId = selectedRecipientType.slice('list:'.length);
      const list = savedLists.find((l) => l.id === listId);
      if (!list || !list.emails || list.emails.length === 0) {
        throw new Error('That saved list is empty or no longer exists.');
      }
      recipientType = 'custom';
      customEmailsPayload = list.emails
        .map((e) => (typeof e === 'string' ? e : e?.email || ''))
        .filter(Boolean);
    } else if (selectedRecipientType !== 'custom') {
      customEmailsPayload = [];
    }

    return {
      subject: emailSubject,
      message: emailContent,
      recipient_type: recipientType,
      custom_emails: customEmailsPayload,
    };
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      setEmailStatus({ type: 'error', message: 'Please enter a subject and message.' });
      return;
    }

    setEmailLoading(true);
    setEmailStatus(null);

    try {
      const payload = buildSendPayload();

      if (scheduleEnabled) {
        if (!scheduleAt) throw new Error('Pick a date/time for the scheduled send.');
        const when = new Date(scheduleAt);
        if (Number.isNaN(when.getTime())) throw new Error('Invalid send date/time.');
        if (when.getTime() <= Date.now()) throw new Error('Pick a date/time in the future.');

        const res = await fetch(`${API_URL}/api/email/schedule`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, send_at: when.toISOString() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
        setEmailStatus({ type: 'success', message: `Email scheduled for ${when.toLocaleString()}.` });
        setEmailSubject('');
        setEmailContent('');
        setScheduleEnabled(false);
        setScheduleAt('');
        fetchEmailData();
        return;
      }

      const response = await fetch(`${API_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || `Server error (HTTP ${response.status})`);
      }
      if (!data.success) {
        throw new Error(data.detail || 'Failed to send email');
      }
      if (data.successful === 0) {
        const err = data.last_error || '';
        let hint = 'Check that your recipient list contains valid addresses and that the SendGrid sender is verified.';
        if (err.includes('Maximum credits exceeded')) {
          hint = 'Your SendGrid account has run out of credits. Upgrade the plan at app.sendgrid.com → Settings → Account Details → Plan, or wait for the monthly quota to reset.';
        } else if (err.includes('401')) {
          hint = 'SendGrid rejected the request as unauthorized — the API key is likely invalid or expired. Rotate it at app.sendgrid.com → Settings → API Keys.';
        } else if (err.includes('403')) {
          hint = 'SendGrid returned Forbidden — the sender email address is not verified. Verify it at app.sendgrid.com → Settings → Sender Authentication.';
        } else if (err) {
          hint = `SendGrid said: ${err}`;
        }
        setEmailStatus({
          type: 'error',
          message: `0 of ${data.total} recipients delivered (${data.failed} failed). ${hint}`,
        });
      } else {
        setEmailStatus({
          type: 'success',
          message: `${data.message}${data.failed ? ` (${data.failed} failed)` : ''}`,
        });
        setEmailSubject('');
        setEmailContent('');
      }
      fetchEmailData();
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to send email.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const importEmailList = async () => {
    if (!importData.trim() || !importListName.trim()) {
      setEmailStatus({ type: 'error', message: 'Please enter list name and email data.' });
      return;
    }
    setEmailLoading(true);
    try {
      const lines = importData.split('\n').filter((l) => l.trim());
      const emails = [];
      lines.forEach((line) => {
        const parts = line.split(',');
        const emailCell = (parts[0] || '').trim();
        const nameCell = (parts.slice(1).join(',') || '').trim();
        const candidates = emailCell.split(/[;\s]+/).map((e) => e.trim()).filter(Boolean);
        if (candidates.length === 1) {
          emails.push({ email: candidates[0], name: nameCell });
        } else {
          candidates.forEach((e) => emails.push({ email: e, name: '' }));
        }
      });
      const validEmails = emails.filter((e) => e.email && e.email.includes('@'));

      const response = await fetch(`${API_URL}/api/email/import-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: validEmails, list_name: importListName }),
      });
      const data = await response.json();
      if (data.success) {
        setEmailStatus({ type: 'success', message: data.message });
        setShowImportModal(false);
        setImportData('');
        setImportListName('');
        fetchEmailData();
      } else {
        throw new Error(data.detail || 'Failed to import list');
      }
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message || 'Failed to import email list.' });
    } finally {
      setEmailLoading(false);
    }
  };

  const cancelScheduledEmail = async (s) => {
    if (!window.confirm(`Cancel scheduled email "${s.subject}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/email/scheduled/${s.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to cancel');
      setScheduledList((prev) =>
        prev.map((x) => (x.id === s.id ? { ...x, status: 'cancelled' } : x))
      );
      setEmailStatus({ type: 'success', message: 'Scheduled email cancelled.' });
    } catch (err) {
      setEmailStatus({ type: 'error', message: err.message });
    }
  };

  // --------------- Render ---------------
  return (
    <div className="email-center">
      <SendGridHealthBadge />

      {emailStatus && (
        <div className={`sync-status ${emailStatus.type}`}>
          {emailStatus.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {emailStatus.message}
        </div>
      )}

      <EmailAnalyticsBar analytics={emailAnalytics} />

      <div className="email-grid">
        <ComposeEmailCard
          emailRecipients={emailRecipients}
          savedLists={savedLists}
          selectedRecipientType={selectedRecipientType}
          setSelectedRecipientType={setSelectedRecipientType}
          customEmails={customEmails}
          setCustomEmails={setCustomEmails}
          emailSubject={emailSubject}
          setEmailSubject={setEmailSubject}
          emailContent={emailContent}
          setEmailContent={setEmailContent}
          scheduleEnabled={scheduleEnabled}
          setScheduleEnabled={setScheduleEnabled}
          scheduleAt={scheduleAt}
          setScheduleAt={setScheduleAt}
          emailLoading={emailLoading}
          onSend={sendEmail}
          onSaveCurrentAsTemplate={saveCurrentAsTemplate}
        />

        <div className="email-sidebar">
          <TemplatesCard
            templates={emailTemplates}
            onLoad={loadTemplate}
            onDelete={deleteTemplate}
          />

          <ImportListCard onOpen={() => setShowImportModal(true)} />

          <SavedListsCard
            savedLists={savedLists}
            onUseList={useSavedList}
            onDeleteList={deleteSavedList}
          />

          <ScheduledEmailsCard
            scheduledList={scheduledList}
            onCancel={cancelScheduledEmail}
          />

          <EmailHistoryCard
            emailHistory={emailHistory}
            onViewStats={viewEmailStats}
            onReuse={reuseEmail}
            onSaveAsTemplate={saveAsTemplate}
            onDelete={deleteEmailHistory}
          />
        </div>
      </div>

      <ImportEmailListModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        listName={importListName}
        setListName={setImportListName}
        listData={importData}
        setListData={setImportData}
        onSubmit={importEmailList}
        loading={emailLoading}
      />

      {showStatsModal && (
        <EmailStatsModal
          stats={selectedEmailStats}
          onClose={() => setShowStatsModal(false)}
          onReuse={(item, e) => {
            setShowStatsModal(false);
            reuseEmail(item, e);
          }}
          onSaveAsTemplate={saveAsTemplate}
        />
      )}
    </div>
  );
};

export default EmailCenterTab;
